import { useState } from "react";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { productsHttp } from "./products-http";
import { OrganizationKeyName } from "../../../model/Organization";
import {
  CreateProductPayload,
  GetProductListActionFilters,
  Product,
  ProductListItem,
} from "../models/product";
import { config } from "../../../config";

const cacheKeys = {
  products: "products",
  product: "product",
};

export const useDuplicateProduct = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { mutateAsync: createProduct } = useCreateProduct();

  const duplicateProduct = async (id: string, suffix?: string) => {
    setIsLoading(true);
    const p = await productsHttp.getProduct(organizationKeyName, id);
    if (!p) {
      throw new Error("Product not found");
    }
    const {
      id: pId,
      dimensions,
      additionGroups,
      acceptedPaymentTypes,
      packagingInstructions,
      imageUrl,
      isEligibleForDiscount,
      tags,
      labels,
      ...rest
    } = p;
    const regularImageUrl = imageUrl ? imageUrl.split(".x-small").join("") : "";
    const productToCreate: CreateProductPayload = {
      ...rest,
      tags: tags.map((tag) => tag.id),
      labels: labels.map((label) => label.id),
      ...(regularImageUrl && { imageUrl: regularImageUrl }),
      isEligibleForDiscount:
        isEligibleForDiscount !== undefined ? isEligibleForDiscount : true,
    };
    if (dimensions) {
      if (dimensions.height) {
        productToCreate.height = dimensions.height;
      }
      if (dimensions.width) {
        productToCreate.width = dimensions.width;
      }
      if (dimensions.length) {
        productToCreate.length = dimensions.length;
      }
    }
    if (additionGroups && additionGroups.length > 0) {
      productToCreate.additionGroups = additionGroups.map(
        (ag: { id: string; name: string; title: string }) => ag.id
      );
    }
    if (packagingInstructions && packagingInstructions.length > 0) {
      productToCreate.productPackagingInstructions = packagingInstructions.map(
        (pi: { id: string; amount: number }) => ({
          id: pi.id,
          amount: pi.amount,
        })
      );
    }
    if (acceptedPaymentTypes && !Array.isArray(acceptedPaymentTypes)) {
      productToCreate.acceptedPaymentTypes = [];
    } else {
      productToCreate.acceptedPaymentTypes = acceptedPaymentTypes;
    }
    const response = await createProduct({
      data: {
        ...productToCreate,
        name: suffix ? `${p?.name} ${suffix}` : `Duplicate of ${p?.name}`,
        title: p?.title,
      },
      organizationKeyName,
    });
    setIsLoading(false);
    return response;
  };

  return { isLoading, duplicateProduct };
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation(productsHttp.createProduct, {
    onSuccess: async (id) => {
      await queryClient.refetchQueries(cacheKeys.products);
      setTimeout(() => {
        queryClient.refetchQueries([cacheKeys.product, id]);
      }, 3000);
    },
  });
};

export const useListShopProducts = (
  organizationKeyName: OrganizationKeyName,
  filters: GetProductListActionFilters,
  enabled: boolean = true
) => {
  return useQuery<ProductListItem[], any>(
    [cacheKeys.products, filters, organizationKeyName],
    () => productsHttp.getShopProductList(organizationKeyName, filters),
    { refetchOnWindowFocus: false, enabled }
  );
};
export const useListProducts_ = (
  organizationKeyName: OrganizationKeyName,
  filters: GetProductListActionFilters,
  enabled: boolean = true
) => {
  return useInfiniteQuery({
    queryKey: [cacheKeys.products, filters, organizationKeyName],
    queryFn: async ({
      pageParam = `${config.apiEndpoint}/organizations/${organizationKeyName}/shop-products`,
    }) => {
      const res = await productsHttp.getProductList_({
        url: pageParam,
        filters,
      });
      return res;
    },

    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.links?.next_page;

      if (!nextPage) {
        return undefined;
      }
      return nextPage;
    },
    enabled: !!organizationKeyName && enabled,
  });
};

export const useGetProduct = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<Product | undefined, any>(
    [cacheKeys.product, id],
    () => productsHttp.getProduct(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useArchiveProduct = () => {
  const queryClient = useQueryClient();

  return useMutation(productsHttp.archiveProduct, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.products);
    },
  });
};

export const useUpdateProduct = (id?: string) => {
  const queryClient = useQueryClient();

  return useMutation(productsHttp.updateProduct, {
    onSuccess: async () => {
      await queryClient.refetchQueries(cacheKeys.products);
      if (id) {
        await queryClient.refetchQueries([cacheKeys.product, id]);
      }
    },
  });
};

export const useUploadProductImage = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation(productsHttp.uploadProductImage, {
    onSuccess: async () => {
      setTimeout(async () => {
        await queryClient.refetchQueries(cacheKeys.products);
        if (id) {
          await queryClient.refetchQueries([cacheKeys.product, id]);
        }
      }, 2000);
    },
  });
};
